import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  StarIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  FolderIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import api from '../../config/api';
import { toast } from 'react-hot-toast';

// 学科定义
interface Subject {
  id: string;
  name: string;
  icon?: string;
}

// 知识点节点
interface KnowledgePointNode {
  id: string;
  name: string;
  subject: string;
  level: number;
  content?: string;
  summary?: string;
  children: KnowledgePointNode[];
  questionCount?: number;
  averageMastery?: number;
}

const SUBJECT_ICONS: Record<string, React.ReactNode> = {
  math: <span className="text-2xl">📐</span>,
  english: <span className="text-2xl">🔤</span>,
  chinese: <span className="text-2xl">📚</span>,
  physics: <span className="text-2xl">⚛️</span>,
  chemistry: <span className="text-2xl">🧪</span>,
  biology: <span className="text-2xl">🧬</span>,
  history: <span className="text-2xl">🏛️</span>,
  geography: <span className="text-2xl">🌍</span>,
  programming: <span className="text-2xl">💻</span>,
  computer: <span className="text-2xl">🖥️</span>,
  other: <span className="text-2xl">📖</span>
};

const KnowledgeBase: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [knowledgeTree, setKnowledgeTree] = useState<KnowledgePointNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [popularPoints, setPopularPoints] = useState<any[]>([]);

  // 加载学科列表
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await api.get('/knowledge/subjects');
        const data = response.data?.data || response.data;
        setSubjects(data);
        if (data.length > 0) {
          setSelectedSubject(data[0].id);
        }
      } catch (error) {
        console.error('加载学科失败:', error);
      }
    };
    fetchSubjects();
  }, []);

  // 加载知识点树
  useEffect(() => {
    if (!selectedSubject) return;

    const fetchTree = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/knowledge/points/${selectedSubject}`);
        const data = response.data?.data || response.data;
        setKnowledgeTree(data);
      } catch (error) {
        console.error('加载知识点失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTree();
  }, [selectedSubject]);

  // 加载热门知识点
  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const response = await api.get('/knowledge/popular', {
          params: { limit: 6 }
        });
        const data = response.data?.data || response.data;
        setPopularPoints(data);
      } catch (error) {
        console.error('加载热门知识点失败:', error);
      }
    };
    fetchPopular();
  }, []);

  // 搜索
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await api.get('/knowledge/search', {
        params: { q: searchQuery, semantic: 'true' }
      });
      const data = response.data?.data || response.data;
      setSearchResults(data);
    } catch (error) {
      console.error('搜索失败:', error);
      toast.error('搜索失败');
    } finally {
      setSearching(false);
    }
  };

  // 切换节点展开
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // 渲染知识点树节点
  const renderTreeNode = (node: KnowledgePointNode, depth: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors ${
            depth === 0 ? 'hover:bg-blue-50' : 'hover:bg-gray-50'
          }`}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
        >
          {hasChildren && (
            <button
              onClick={() => toggleNode(node.id)}
              className="mr-2 text-gray-400 hover:text-gray-600"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          )}
          {!hasChildren && <span className="w-6" />}
          
          <FolderIcon className={`w-4 h-4 mr-2 ${depth === 0 ? 'text-blue-500' : 'text-gray-400'}`} />
          
          <Link
            to={`/student/knowledge/${node.id}`}
            className="flex-1 text-sm text-gray-700 hover:text-blue-600"
          >
            {node.name}
          </Link>

          {node.questionCount !== undefined && node.questionCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
              {node.questionCount}题
            </span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <BookOpenIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">知识库</h1>
              <p className="text-blue-100">系统化的知识体系，助力高效学习</p>
            </div>
          </div>

          {/* 搜索框 */}
          <div className="relative max-w-2xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="搜索知识点、概念、公式..."
              className="w-full px-4 py-3 pl-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              {searching ? '搜索中...' : '搜索'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 搜索结果 */}
        {searchResults.length > 0 && (
          <div className="mb-6 bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              搜索结果 ({searchResults.length})
            </h3>
            <div className="space-y-3">
              {searchResults.map((result: any) => (
                <Link
                  key={result.knowledgePoint.id}
                  to={`/student/knowledge/${result.knowledgePoint.id}`}
                  className="block p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {result.knowledgePoint.name}
                      </h4>
                      {result.knowledgePoint.summary && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {result.knowledgePoint.summary}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      result.matchType === 'semantic' 
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {result.matchType === 'semantic' ? '语义匹配' : '精确匹配'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <button
              onClick={() => setSearchResults([])}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700"
            >
              清除搜索结果
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 学科侧边栏 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                学科分类
              </h3>
              <div className="space-y-1">
                {subjects.map(subject => (
                  <button
                    key={subject.id}
                    onClick={() => setSelectedSubject(subject.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      selectedSubject === subject.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {SUBJECT_ICONS[subject.id] || SUBJECT_ICONS.other}
                    <span className="font-medium">{subject.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 热门知识点 */}
            {popularPoints.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4" />
                  热门知识点
                </h3>
                <div className="space-y-2">
                  {popularPoints.slice(0, 5).map((point: any) => (
                    <Link
                      key={point.id}
                      to={`/student/knowledge/${point.id}`}
                      className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {point.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 知识点树 */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {subjects.find(s => s.id === selectedSubject)?.name || '知识点'}体系
                </h2>
                <span className="text-sm text-gray-500">
                  {knowledgeTree.length} 个一级知识点
                </span>
              </div>

              {loading ? (
                <div className="py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-3 text-gray-500">加载中...</p>
                </div>
              ) : knowledgeTree.length === 0 ? (
                <div className="py-12 text-center">
                  <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无知识点数据</p>
                </div>
              ) : (
                <div className="border border-gray-100 rounded-xl divide-y divide-gray-50">
                  {knowledgeTree.map(node => renderTreeNode(node))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
